/**
 * Selleasy upsell × Horizon cart-drawer stacking fix.
 *
 * Why: Horizon's cart drawer is a native <dialog> opened with showModal(),
 * which places it in the browser's top-layer. Elements in the top-layer
 * render above ALL normal DOM regardless of z-index. Selleasy renders
 * <lb-upsell-addon-popup> in normal DOM, so z-index alone cannot beat it.
 *
 * Strategy: when the Selleasy popup becomes visible, call dialog.close() to
 * remove the cart drawer from the top-layer. If the Selleasy popup closes
 * without the user navigating to checkout, reopen the drawer with showModal().
 */
(function () {
  'use strict';

  // Horizon uses `dialog.cart-drawer__dialog`. Older/variant themes may use
  // `dialog.cart-drawer`. We intentionally do NOT match bare `.dialog-drawer`
  // because that also applies to the account drawer.
  var CART_DRAWER_SELECTOR = 'dialog.cart-drawer__dialog, dialog.cart-drawer';
  var UPSELL_POPUP_SELECTOR = 'lb-upsell-addon-popup';
  var CHECKOUT_TRIGGER_SELECTOR =
    '[name="checkout"], button[name="checkout"], input[name="checkout"], a[href*="/checkout"]';
  var ACTIVE_BODY_CLASS = 'selleasy-upsell-active';

  var cartDrawerRef = null;
  var dialogWasOpen = false;
  var isDemoting = false;
  var pendingReopen = false;
  var lastPopupVisible = false;

  function getCartDrawer() {
    // Prefer a currently-open match so we never act on a stale stale reference.
    var all = document.querySelectorAll(CART_DRAWER_SELECTOR);
    for (var i = 0; i < all.length; i++) {
      if (all[i].open) {
        cartDrawerRef = all[i];
        return cartDrawerRef;
      }
    }
    if (cartDrawerRef && document.body && document.body.contains(cartDrawerRef)) {
      return cartDrawerRef;
    }
    cartDrawerRef = all[0] || null;
    return cartDrawerRef;
  }

  function isElVisible(el) {
    if (!el) return false;
    if (el.hidden) return false;
    var s = window.getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0') return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function isPopupVisible(popup) {
    // Selleasy emits a host <lb-upsell-addon-popup> that may render as a 0-height
    // block, plus sibling nodes `.lb-addon-popup-container` and `.lb-addon-popup-bg`
    // attached at body level. The popup is "open" if the container or bg is
    // rendered. Checking the host alone is unreliable (height 0 when closed AND open).
    if (!popup) return false;
    if (popup.hidden) return false;
    var hostStyle = window.getComputedStyle(popup);
    if (hostStyle.display === 'none' || hostStyle.visibility === 'hidden') return false;
    var container = document.querySelector('.lb-addon-popup-container');
    var bg = document.querySelector('.lb-addon-popup-bg');
    return isElVisible(container) || isElVisible(bg);
  }

  function demoteCartDrawer() {
    var drawer = getCartDrawer();
    if (!drawer || !drawer.open) return;
    dialogWasOpen = true;
    isDemoting = true;
    try {
      drawer.close();
    } catch (err) {
      drawer.removeAttribute('open');
    }
    // isDemoting is cleared by the 'close' listener below — NOT here. The
    // dialog 'close' event is dispatched as a queued task; clearing on a
    // microtask would race ahead of that dispatch and leak a user-close
    // through the wrong branch.
    document.body.classList.add(ACTIVE_BODY_CLASS);
  }

  function restoreCartDrawer() {
    document.body.classList.remove(ACTIVE_BODY_CLASS);
    var drawer = getCartDrawer();
    if (!drawer || !dialogWasOpen) return;
    dialogWasOpen = false;
    try {
      if (typeof drawer.showModal === 'function' && !drawer.open) {
        drawer.showModal();
      } else if (!drawer.open) {
        drawer.setAttribute('open', '');
      }
    } catch (err) {
      drawer.setAttribute('open', '');
    }
  }

  function syncState() {
    var popup = document.querySelector(UPSELL_POPUP_SELECTOR);
    var visible = isPopupVisible(popup);
    if (visible === lastPopupVisible) return;
    lastPopupVisible = visible;

    var drawer = getCartDrawer();
    if (visible) {
      if (drawer && drawer.open) demoteCartDrawer();
    } else if (dialogWasOpen && pendingReopen) {
      pendingReopen = false;
      restoreCartDrawer();
    }
  }

  // Capture-phase click: mark intent to reopen only when the checkout action
  // originates inside the cart drawer itself.
  document.addEventListener(
    'click',
    function (e) {
      var trigger = e.target.closest && e.target.closest(CHECKOUT_TRIGGER_SELECTOR);
      if (!trigger) return;
      var drawer = getCartDrawer();
      if (!drawer || !drawer.contains(trigger)) return;
      pendingReopen = true;
    },
    true
  );

  // Distinguish our programmatic demote-close from a user-initiated close.
  // The programmatic case preserves pending flags so restore can fire later;
  // the user case clears them so we never resurrect a drawer the user dismissed.
  document.addEventListener(
    'close',
    function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (!t.matches(CART_DRAWER_SELECTOR)) return;
      if (isDemoting) {
        isDemoting = false;
        return;
      }
      pendingReopen = false;
      dialogWasOpen = false;
    },
    true
  );

  // Primary: MutationObserver on body for attribute + subtree changes.
  var observer = null;
  if (typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(syncState);
  }

  function startObserving() {
    if (!document.body) {
      document.addEventListener('DOMContentLoaded', startObserving, { once: true });
      return;
    }
    if (observer) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'aria-hidden']
      });
    }
    // Fallback poller — cheap safety net covering environments where Selleasy
    // mutates shadow DOM or attributes the observer doesn't fire on.
    setInterval(syncState, 800);
    syncState();
  }

  startObserving();
})();
